import type { ItemFieldRow } from './item-field-reader';
import type { FromBasePlayerInput, FromBaseRow } from './from-base-context';
import { fromBaseClone, fromBaseRecord, fromBaseUuid, FromBaseStageMissing } from './from-base-context';
import type { FromBasePlayerResourceMetadata } from './from-base-player-resources';
import { createFromBasePlayerGrantContext } from './from-base-player-grants';
import { createAbilityConditionEvaluator } from './ability-conditions';
import { createAbilityEssenceSourceCollector } from './ability-essence-sources';
import { createDynamicIntegerSourceCollector } from './dynamic-integer-sources';
import { statusIntegerFamilyComputedByWalk } from './integer-layer-rollup';
import { createCatalogDefaultProviderReader } from './provider-catalog-order';
import { readNodeItemId } from './layer-cell-readers';
import { readLayerListEntryUuid } from './inventory-raw-readers';
import { foldCreatureDefenseEntries } from './creature-defense';
import { DEFENSE_SLOTS } from './creature-defense-delta';
import { isAuraPassive } from './passive-integer-fold';
import { constructFromBaseReachWatch } from './from-base-reach-watch';
import { phpJsonInt, phpJsonString, phpJsonTruthy } from './php-json-values';

/** Native late feat modifiers, carrier defense fold and quiet reach-watch facts.
 * Effectful witness/presence provider contracts retain their named producer gap. */
export function constructPlayerPostFinalizerFromBase(base: FromBasePlayerInput, metadata: FromBasePlayerResourceMetadata, input: FromBaseRow): FromBaseRow {
    const host = fromBaseClone(input), { readers, definition, common, deps } = createFromBasePlayerGrantContext(base, metadata, host, 'post-finalizer-clusters');
    const stored = (row: ItemFieldRow | null, key: string): unknown => readers.itemField({ [key]: [row ? (row as FromBaseRow)[key] ?? null : null] }, key);
    const entries = (row: ItemFieldRow | null, key: string) => Object.values(readers.layerEntries(row, key));
    const list = (row: ItemFieldRow | null, key: string): unknown[] => { const slot = stored(row, key); return slot !== null && typeof slot === 'object' ? Object.values(slot) : []; };
    const creature = (host.computed as FromBaseRow).creature as FromBaseRow;
    const dynamic = createDynamicIntegerSourceCollector({ ...deps, metadata: metadata.rollup, inventoryAbilityTypes: metadata.rollup.inventoryAbilityTypes });
    const feats = dynamic.resolvedFeats(host);
    for (const layer of metadata.rollup.integerLayers) {
        if (!statusIntegerFamilyComputedByWalk(layer, metadata.rollup)) continue;
        const contribution = dynamic.featContribution(host, layer, feats);
        if (contribution.total === 0 && !contribution.sources.length) continue;
        const sourceBuckets = fromBaseRecord(creature.modifier_sources) ? creature.modifier_sources : {};
        const previous = list(sourceBuckets, layer).filter(row => !fromBaseRecord(row) || stored(row, 'kind') !== 'feat');
        if (previous.some(row => fromBaseRecord(row) && stored(row, 'kind') === 'inventory_equipped'
            && stored(row, 'value_brace') !== null && !phpJsonTruthy(stored(row, 'suppressed')))) continue;
        const modifiers = fromBaseRecord(creature.modifiers) ? creature.modifiers : {};
        const total = phpJsonInt(stored(modifiers, layer)) + contribution.total;
        if (total) modifiers[layer] = total; else delete modifiers[layer];
        sourceBuckets[layer] = [...previous, ...contribution.sources];
        creature.modifiers = modifiers; creature.modifier_sources = sourceBuckets;
    }
    const conditions = createAbilityConditionEvaluator(deps), levelGate = createAbilityEssenceSourceCollector({ ...deps, metadata: metadata.preliminary }).levelGate(host);
    const providers = createCatalogDefaultProviderReader({ ...common, nodeItemId: readNodeItemId });
    const seen = new Set<string>(), self = readNodeItemId(host); if (self) seen.add(self);
    const ownEntries = (carrier: ItemFieldRow, key: string): unknown[] => {
        const layers = stored(carrier, 'item_layer_types'); if (!fromBaseRecord(layers)) return [];
        const slot = stored(layers, key);
        if (slot == null) return [];
        return slot !== null && typeof slot === 'object' && Object.prototype.hasOwnProperty.call(slot, '0')
            && stored(slot as ItemFieldRow, 'items') === null && stored(slot as ItemFieldRow, 'item_layer_types') === null ? Object.values(slot) : [slot];
    };
    const fold = (carrier: ItemFieldRow, kind: unknown): void => {
        const source = { source_item_id: readers.itemField(carrier, 'item_id'), source_name: readers.itemField(carrier, 'item_name') ?? 'Creature classification',
            source_kind: kind ?? readers.itemField(carrier, 'item_type') ?? 'carrier' };
        for (const [key, descriptor] of Object.entries(DEFENSE_SLOTS)) for (const entry of ownEntries(carrier, key)) {
            const id = fromBaseRecord(entry) && typeof stored(entry, 'damage_type') === 'string'
                ? phpJsonString(stored(entry, 'damage_type')) : readLayerListEntryUuid(entry);
            if (!fromBaseUuid(id)) continue;
            const defenses = fromBaseRecord(creature.defenses) ? creature.defenses : {};
            defenses[descriptor.slot] = foldCreatureDefenseEntries({ family: descriptor.key === 'damage_type' ? 'damage' : 'status',
                initialEntries: list(defenses, descriptor.slot), events: [{ uuid: id, source }] });
            creature.defenses = defenses;
        }
    };
    const walk = (node: ItemFieldRow): void => {
        for (const carrier of providers.providers(node)) {
            const id = readNodeItemId(carrier); if (id && seen.has(id)) continue; if (id) seen.add(id);
            fold(carrier, null);
            for (const kind of ['feat', 'passive_ability']) for (const entry of ownEntries(carrier, kind)) {
                const grantId = readLayerListEntryUuid(entry); if (!fromBaseUuid(grantId) || seen.has(grantId)) continue;
                const nested = fromBaseRecord(entry) ? stored(entry, 'item_arr') : null;
                const ability = fromBaseRecord(nested) ? nested : definition(grantId);
                if (!ability || !phpJsonTruthy(stored(ability, 'item_layer_types')) || isAuraPassive(ability, readers.layerValue)
                    || levelGate && !levelGate({ source: readers.itemField(carrier, 'item_id') }, ability)) continue;
                seen.add(grantId);
                if (conditions.check(ability, host, false, false)) fold(ability, kind);
            }
            walk(carrier);
        }
    };
    walk(host);
    for (const layer of ['force_move_resistance', 'immune_to_forced_movement'])
        if (readers.layerValue(host, layer) !== null || entries(host, layer).length)
            throw new FromBaseStageMissing('post-finalizer-clusters', `forced-movement-provider-sources:${layer}`);
    constructFromBaseReachWatch(host, readers, id => definition(id) as FromBaseRow | null);
    return host;
}
